import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { wholesaleSchema, WholesaleFormData } from '../../lib/validation';
import { useWholesaleItem, useCreateWholesale, useUpdateWholesale } from '../../hooks/useWholesale';

export function WholesaleForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const wholesaleId = id ? parseInt(id) : 0;

  const { data: wholesaleData, isLoading: isLoadingItem } = useWholesaleItem(wholesaleId);
  const createMutation = useCreateWholesale();
  const updateMutation = useUpdateWholesale();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<WholesaleFormData>({
    resolver: zodResolver(wholesaleSchema),
    defaultValues: {
      nombreProducto: '',
      tipo: '',
      precio: 0,
      precioDescuento: undefined,
      precioVolumen: undefined,
      cantidadVolumen: undefined,
      stockActual: 0,
      stockMinimo: 0,
      proveedor: '',
      codigoBarras: '',
      notas: '',
    },
  });

  useEffect(() => {
    if (isEditing && wholesaleData) {
      const data = wholesaleData.data;
      setValue('nombreProducto', data.nombreProducto);
      setValue('tipo', data.tipo || '');
      setValue('precio', Number(data.precio || 0));
      setValue('precioDescuento', data.precioDescuento ? Number(data.precioDescuento) : undefined);
      setValue('precioVolumen', data.precioVolumen ? Number(data.precioVolumen) : undefined);
      setValue('cantidadVolumen', data.cantidadVolumen || undefined);
      setValue('stockActual', data.stockActual || 0);
      setValue('stockMinimo', data.stockMinimo || 0);
      setValue('proveedor', data.proveedor || '');
      setValue('codigoBarras', data.codigoBarras || '');
      setValue('notas', data.notas || '');
    }
  }, [isEditing, wholesaleData, setValue]);

  const onSubmit = async (data: WholesaleFormData) => {
    try {
      if (isEditing && id) {
        await updateMutation.mutateAsync({ id: parseInt(id), data });
      } else {
        await createMutation.mutateAsync(data);
      }
      navigate('/mayoristas');
    } catch (error) {
      // Error ya manejado por react-query
    }
  };

  if (isLoadingItem) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/mayoristas')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditing ? 'Editar Precio Mayorista' : 'Nuevo Precio Mayorista'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl shadow-sm p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre del producto *
            </label>
            <Input
              {...register('nombreProducto')}
              placeholder="Nombre del producto mayorista"
              error={errors.nombreProducto?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <Input
              {...register('tipo')}
              placeholder="Ej: Electrónica, Ropa, etc."
              error={errors.tipo?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Código de barras
            </label>
            <Input
              {...register('codigoBarras')}
              placeholder="Código de barras"
              error={errors.codigoBarras?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Precio *
            </label>
            <Input
              type="number"
              step="0.01"
              {...register('precio', { valueAsNumber: true })}
              placeholder="0.00"
              error={errors.precio?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Precio con Descuento
            </label>
            <Input
              type="number"
              step="0.01"
              {...register('precioDescuento', { valueAsNumber: true })}
              placeholder="0.00"
              error={errors.precioDescuento?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Precio por Volumen
            </label>
            <Input
              type="number"
              step="0.01"
              {...register('precioVolumen', { valueAsNumber: true })}
              placeholder="0.00"
              error={errors.precioVolumen?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cantidad para Volumen
            </label>
            <Input
              type="number"
              {...register('cantidadVolumen', { valueAsNumber: true })}
              placeholder="Cantidad mínima"
              error={errors.cantidadVolumen?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stock Actual
            </label>
            <Input
              type="number"
              {...register('stockActual', { valueAsNumber: true })}
              placeholder="0"
              error={errors.stockActual?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stock Mínimo
            </label>
            <Input
              type="number"
              {...register('stockMinimo', { valueAsNumber: true })}
              placeholder="0"
              error={errors.stockMinimo?.message}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proveedor
            </label>
            <Input
              {...register('proveedor')}
              placeholder="Nombre del proveedor"
              error={errors.proveedor?.message}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              {...register('notas')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Notas adicionales..."
            />
          </div>
        </div>

        <div className="flex gap-3 pt-4 border-t">
          <Button 
            type="submit" 
            disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}
          >
            <Save size={18} />
            {isSubmitting || createMutation.isPending || updateMutation.isPending 
              ? 'Guardando...' 
              : isEditing ? 'Actualizar' : 'Guardar'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/mayoristas')}
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
}